import { useState, useCallback, useEffect, useRef } from 'react';
import { logger } from 'utils/logger';
import { useSeonSessionMutation } from './webapi/client/useSeonSessionMutation';

interface SeonConfig {
  host: string;
  session_id: string;
  audio_fingerprint: boolean;
  canvas_fingerprint: boolean;
  webgl_fingerprint: boolean;
  max_proxy_delay: number;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface SeonInstance {
  config: (body: SeonConfig) => void;
  getBase64Session: () => Promise<string>;
}

declare global {
  interface Window {
    seon: SeonInstance;
  }
}

const useSeon = () => {
  const [sessionId, setSessionId] = useState<string | undefined>();
  const isSeonReadyRef = useRef<boolean>(false);
  const { mutateAsync: sendSeonSession } = useSeonSessionMutation();

  const createSeonScript = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.seon) {
        isSeonReadyRef.current = true;
        resolve();
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.getElementById('seon-agent');
      if (existingScript) {
        // Wait for the existing script to load
        existingScript.addEventListener('load', () => {
          isSeonReadyRef.current = true;
          resolve();
        });
        existingScript.addEventListener('error', () => {
          reject(new Error('SEON script failed to load'));
        });
        return;
      }

      const seonScript = document.createElement('script');
      seonScript.src = 'https://cdn.seondf.com/js/v5/agent.js';
      seonScript.async = true;
      seonScript.defer = true;
      seonScript.id = 'seon-agent';

      seonScript.onload = () => {
        isSeonReadyRef.current = true;
        resolve();
      };

      seonScript.onerror = () => {
        reject(new Error('SEON script failed to load'));
      };

      document.head.appendChild(seonScript);
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await createSeonScript();
      } catch (error) {
        logger.error(
          'SEON script failed to load',
          undefined,
          error instanceof Error ? error : undefined,
        );
      }
    })();
  }, [createSeonScript]);

  const getSessionId = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;

    if (!isSeonReadyRef.current) {
      logger.info('SEON not ready - script may still be loading');
    }

    try {
      const seonSessionId = await window.seon.getBase64Session();
      setSessionId(seonSessionId);
      return seonSessionId;
    } catch (error) {
      logger.error(
        'Failed to get SEON session ID',
        undefined,
        error instanceof Error ? error : undefined,
      );
      throw new Error(
        `Failed to get SEON session ID: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }, [sessionId]);

  const sendSeonFraudInfo = useCallback(async (): Promise<void> => {
    try {
      if (!window.seon) {
        throw new Error('SEON not loaded - window.seon is undefined');
      }

      const seonSessionId = await getSessionId();

      // Configure SEON with error handling
      window.seon.config({
        host: 'seondf.com',
        session_id: seonSessionId,
        audio_fingerprint: true,
        canvas_fingerprint: true,
        webgl_fingerprint: true,
        max_proxy_delay: 1000,
        onSuccess: (message: string) => {
          logger.info('SEON config success', { message });
        },
        onError: (message: string) => {
          logger.error('SEON config error', { message });
        },
      });

      // Send session data to backend
      await sendSeonSession({
        session: seonSessionId,
      });
    } catch (error) {
      logger.error(
        'Failed to send SEON fraud info',
        undefined,
        error instanceof Error ? error : undefined,
      );
      throw error; // Re-throw to allow calling code to handle
    }
  }, [getSessionId, sendSeonSession]);

  return {
    sendSeonFraudInfo,
    createSeonScript,
  };
};

export default useSeon;
